import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Popover.module.css";

export type PopoverPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end";

export interface PopoverProps {
  /** Conteúdo que dispara o popover (trigger) */
  children: ReactNode;
  /** Conteúdo do popover */
  content: ReactNode;
  /** Posição preferida */
  placement?: PopoverPlacement;
  /** Delay para abrir (ms) */
  openDelay?: number;
  /** Delay para fechar (ms) */
  closeDelay?: number;
  /** Se true, abre no clique em vez de hover */
  trigger?: "hover" | "click";
  /** Largura máxima do popover */
  maxWidth?: number | string;
  /** Classe extra no container do popover */
  className?: string;
  /** Desabilita o popover */
  disabled?: boolean;
  /** Offset em px da âncora */
  offset?: number;
}

const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  placement = "top",
  openDelay = 180,
  closeDelay = 120,
  trigger = "hover",
  maxWidth = 320,
  className = "",
  disabled = false,
  offset = 8,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] =
    useState<PopoverPlacement>(placement);

  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;
    let finalPlacement = placement;

    const space = {
      top: triggerRect.top,
      bottom: vh - triggerRect.bottom,
      left: triggerRect.left,
      right: vw - triggerRect.right,
    };

    // Auto-flip se não couber
    if (
      placement.startsWith("top") &&
      space.top < popoverRect.height + offset
    ) {
      finalPlacement = placement.replace("top", "bottom") as PopoverPlacement;
    } else if (
      placement.startsWith("bottom") &&
      space.bottom < popoverRect.height + offset
    ) {
      finalPlacement = placement.replace("bottom", "top") as PopoverPlacement;
    } else if (
      placement.startsWith("left") &&
      space.left < popoverRect.width + offset
    ) {
      finalPlacement = "right";
    } else if (
      placement.startsWith("right") &&
      space.right < popoverRect.width + offset
    ) {
      finalPlacement = "left";
    }

    switch (finalPlacement) {
      case "top":
        top = triggerRect.top + scrollY - popoverRect.height - offset;
        left =
          triggerRect.left +
          scrollX +
          triggerRect.width / 2 -
          popoverRect.width / 2;
        break;
      case "top-start":
        top = triggerRect.top + scrollY - popoverRect.height - offset;
        left = triggerRect.left + scrollX;
        break;
      case "top-end":
        top = triggerRect.top + scrollY - popoverRect.height - offset;
        left = triggerRect.right + scrollX - popoverRect.width;
        break;
      case "bottom":
        top = triggerRect.bottom + scrollY + offset;
        left =
          triggerRect.left +
          scrollX +
          triggerRect.width / 2 -
          popoverRect.width / 2;
        break;
      case "bottom-start":
        top = triggerRect.bottom + scrollY + offset;
        left = triggerRect.left + scrollX;
        break;
      case "bottom-end":
        top = triggerRect.bottom + scrollY + offset;
        left = triggerRect.right + scrollX - popoverRect.width;
        break;
      case "left":
        top =
          triggerRect.top +
          scrollY +
          triggerRect.height / 2 -
          popoverRect.height / 2;
        left = triggerRect.left + scrollX - popoverRect.width - offset;
        break;
      case "right":
        top =
          triggerRect.top +
          scrollY +
          triggerRect.height / 2 -
          popoverRect.height / 2;
        left = triggerRect.right + scrollX + offset;
        break;
    }

    // Clamp horizontal
    left = Math.max(8, Math.min(left, vw - popoverRect.width - 8 + scrollX));
    // Clamp vertical
    top = Math.max(8, Math.min(top, vh - popoverRect.height - 8 + scrollY));

    setCoords({ top, left });
    setActualPlacement(finalPlacement);
  }, [placement, offset]);

  const show = () => {
    if (disabled) return;
    clearTimers();
    openTimer.current = setTimeout(() => {
      setVisible(true);
    }, openDelay);
  };

  const hide = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => {
      setVisible(false);
    }, closeDelay);
  };

  const toggle = () => {
    if (disabled) return;
    clearTimers();
    setVisible((v) => !v);
  };

  useEffect(() => {
    if (visible) {
      // Pequeno delay para o DOM renderizar e medirmos o tamanho real
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [visible, calculatePosition, content]);

  useEffect(() => {
    if (!visible) return;

    const handleScroll = () => calculatePosition();
    const handleResize = () => calculatePosition();
    const handleClickOutside = (e: MouseEvent) => {
      if (
        trigger === "click" &&
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, calculatePosition, trigger]);

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const triggerProps =
    trigger === "hover"
      ? {
          onMouseEnter: show,
          onMouseLeave: hide,
          onFocus: show,
          onBlur: hide,
        }
      : {
          onClick: toggle,
        };

  return (
    <>
      <span
        ref={triggerRef}
        className={styles.trigger}
        {...triggerProps}
        tabIndex={trigger === "click" ? 0 : undefined}
      >
        {children}
      </span>

      {visible &&
        createPortal(
          <div
            ref={popoverRef}
            className={`${styles.popover} ${styles[actualPlacement]} ${className}`}
            style={{
              top: coords.top,
              left: coords.left,
              maxWidth:
                typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
            }}
            role="tooltip"
            onMouseEnter={trigger === "hover" ? show : undefined}
            onMouseLeave={trigger === "hover" ? hide : undefined}
          >
            <div className={styles.arrow} />
            <div className={styles.content}>{content}</div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default Popover;
